import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import MaintenanceSiteToggle from './MaintenanceSiteToggle';
import './AdminNavbar.css';

const AdminNavbar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isSuperAdmin, user } = useAdminAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
    navigate('/admin/login', { replace: true });
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const closeMenus = () => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  };

  useEffect(() => {
    if (!userMenuOpen) return;

    const onPointerDown = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [userMenuOpen]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  const navItems = isSuperAdmin
    ? [
        { path: '/admin/dashboard', label: 'Dashboard' },
        { path: '/admin/reports', label: 'Analíticas' },
        { path: '/admin/folders', label: 'Carpetas' },
        { path: '/admin/photos', label: 'Fotos' },
        { path: '/admin/payments', label: 'Pagos' },
        { path: '/admin/photographers', label: 'Fotógrafos' },
        { path: '/admin/users', label: 'Usuarios' },
        { path: '/admin/promos', label: 'Publicidades' },
        { path: '/admin/settings', label: 'Config' },
      ]
    : [
        { path: '/admin/dashboard', label: 'Dashboard' },
        { path: '/admin/reports', label: 'Analíticas' },
        { path: '/admin/folders', label: 'Carpetas' },
        { path: '/admin/photos', label: 'Fotos' },
        { path: '/admin/payments', label: 'Pagos' },
      ];

  const displayName = user?.display_name || user?.email || 'Admin';
  const initials = displayName
    .split(/[\s@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');

  return (
    <nav className="admin-navbar">
      <div className="admin-navbar-wrapper">
        <Link to="/admin" className="admin-navbar-brand" onClick={closeMenus}>
          <img
            src={`${process.env.PUBLIC_URL}/image.png`}
            alt="SurfShot Logo"
            className="admin-brand-logo"
          />
        </Link>

        <button
          type="button"
          className="admin-mobile-menu-button"
          onClick={() => {
            setUserMenuOpen(false);
            setMobileMenuOpen((open) => !open);
          }}
          aria-label={mobileMenuOpen ? 'Cerrar menú' : 'Abrir menú'}
          aria-expanded={mobileMenuOpen}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            {mobileMenuOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </>
            ) : (
              <>
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </>
            )}
          </svg>
        </button>

        <ul className={`admin-navbar-menu ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {isSuperAdmin && (
            <li className="admin-navbar-maintenance-li mobile-maintenance">
              <MaintenanceSiteToggle variant="compact" />
            </li>
          )}
          {navItems.map((item) => (
            <li key={item.path}>
              <Link
                to={item.path}
                className={`admin-nav-link ${isActive(item.path) ? 'active' : ''}`}
                onClick={closeMenus}
              >
                {item.label}
              </Link>
            </li>
          ))}

          <li className="admin-navbar-mobile-footer">
            <div className="admin-navbar-mobile-user">
              <span className="admin-navbar-mobile-user-name">{displayName}</span>
              {user?.email && user.display_name && (
                <span className="admin-navbar-mobile-user-email">{user.email}</span>
              )}
              <span className="admin-navbar-role-pill">
                {isSuperAdmin ? 'Super admin' : 'Fotógrafo'}
              </span>
            </div>
            <button type="button" className="admin-logout-button" onClick={handleLogout}>
              Cerrar sesión
            </button>
            <a href="/" className="admin-back-button" onClick={closeMenus}>
              <svg className="back-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              Volver al sitio
            </a>
          </li>
        </ul>

        <div className="admin-navbar-toolbar">
          {isSuperAdmin && (
            <div className="admin-navbar-maintenance desktop-maintenance">
              <MaintenanceSiteToggle variant="compact" shortLabels />
            </div>
          )}

          <div className="admin-navbar-user-menu desktop-actions" ref={userMenuRef}>
            <button
              type="button"
              className={`admin-navbar-user-trigger ${userMenuOpen ? 'is-open' : ''}`}
              onClick={() => setUserMenuOpen((open) => !open)}
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
            >
              <span className="admin-navbar-avatar" aria-hidden="true">
                {initials || 'A'}
              </span>
              <span className="admin-navbar-user-meta">
                <span className="admin-navbar-user-name">{displayName}</span>
                <span className="admin-navbar-user-role">
                  {isSuperAdmin ? 'Super admin' : 'Fotógrafo'}
                </span>
              </span>
              <svg
                className="admin-navbar-chevron"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {userMenuOpen && (
              <div className="admin-navbar-dropdown" role="menu">
                <div className="admin-navbar-dropdown-header">
                  <strong>{displayName}</strong>
                  {user?.email && <span>{user.email}</span>}
                </div>
                <a href="/" className="admin-navbar-dropdown-item" role="menuitem" onClick={closeMenus}>
                  Ir al sitio público
                </a>
                <button
                  type="button"
                  className="admin-navbar-dropdown-item admin-navbar-dropdown-item--danger"
                  role="menuitem"
                  onClick={handleLogout}
                >
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          className="admin-mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}
    </nav>
  );
};

export default AdminNavbar;
